import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, DownloadSimple, Receipt, Truck } from "@phosphor-icons/react";
import { api, downloadFile, errorMessage, fmtDate, inr, formatLabel, STATUS_CLASS, STATUS_LABEL } from "@/lib/api";
import { StatusTimeline } from "@/components/store/StatusTimeline";

export default function OrderDetail() {
  const { orderId } = useParams();
  const [busy, setBusy] = useState("");
  const { data: order, isLoading, refetch } = useQuery({ queryKey: ["order", orderId], queryFn: async () => (await api.get(`/orders/${orderId}`)).data });

  const dl = async (key, url, name) => {
    setBusy(key);
    try { await downloadFile(url, name); } catch (e) { toast.error(errorMessage(e, "Download failed")); } finally { setBusy(""); }
  };
  const verify = async () => {
    const { data } = await api.post(`/orders/${orderId}/verify`);
    if (data.status !== "pending") { toast.success("Payment confirmed"); refetch(); } else toast("Still awaiting confirmation");
  };

  if (isLoading) return <div className="mx-auto max-w-5xl px-5 py-24 text-sm text-muted-foreground">Loading…</div>;
  if (!order) return <div className="mx-auto max-w-5xl px-5 py-24">Order not found.</div>;
  const paid = ["paid", "packed", "dispatched", "delivered"].includes(order.status);

  return (
    <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8 md:py-20" data-testid="order-detail-page">
      <Link to="/account" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid="back-to-orders-link"><ArrowLeft /> All orders</Link>
      <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Order {order.order_id}</p>
          <h1 className="mt-2 text-3xl font-medium tracking-tight sm:text-4xl">{fmtDate(order.created_at)}</h1>
        </div>
        <span className={`rounded-full px-4 py-1.5 text-sm font-semibold ${STATUS_CLASS[order.status]}`} data-testid="order-detail-status">{STATUS_LABEL[order.status]}</span>
      </div>

      {order.status === "pending" && (
        <div className="mt-8 rounded-2xl bg-muted p-5 text-sm" data-testid="order-pending-notice">
          Payment for this order hasn't been confirmed. <button onClick={verify} className="font-semibold text-primary underline" data-testid="order-verify-button">Check payment status</button>
        </div>
      )}

      {paid && order.has_physical && (
        <section className="mt-10 rounded-3xl bg-white p-6 ring-1 ring-border/70">
          <StatusTimeline order={order} />
          {order.tracking_number && (
            <div className="mt-6 flex flex-wrap items-start gap-3 rounded-2xl bg-muted p-4 text-sm" data-testid="order-tracking">
              <Truck weight="duotone" size={22} className="text-accent" />
              <div className="flex-1">
                <p className="font-semibold">{order.courier || "Courier"}</p>
                <p className="text-muted-foreground">Tracking ID: <span className="font-mono font-semibold text-foreground">{order.tracking_number}</span></p>
                {order.expected_delivery && <p className="mt-1 text-muted-foreground">Expected by <span className="font-semibold text-foreground">{fmtDate(order.expected_delivery)}</span></p>}
              </div>
              {order.tracking_url && (
                <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-sm bg-foreground px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.1em] text-white transition-colors hover:bg-primary" data-testid="track-parcel-button">
                  Track your parcel
                </a>
              )}
            </div>
          )}
          {order.shipping_address && (
            <p className="mt-4 text-xs text-muted-foreground">Shipping to: {order.shipping_address.line1}, {order.shipping_address.line2 && `${order.shipping_address.line2}, `}{order.shipping_address.city} {order.shipping_address.pincode}, {order.shipping_address.state}</p>
          )}
        </section>
      )}

      <section className="mt-8 grid gap-8 md:grid-cols-12">
        <ul className="space-y-4 md:col-span-7">
          {order.items.map((it) => (
            <li key={it.book_id + it.format} className="flex gap-4 rounded-2xl bg-white p-4 ring-1 ring-border/70" data-testid={`order-item-${it.book_id}-${it.format}`}>
              <img src={it.cover_url} alt="" className="h-24 w-16 rounded object-cover shadow" />
              <div className="flex-1">
                <p className="font-display text-lg font-medium leading-snug">{it.title}</p>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{it.format === "ebook" ? "Ebook PDF" : `${formatLabel(it.format)} × ${it.qty}`}</p>
                {it.dedication && <p className="mt-2 font-display text-sm italic text-foreground/80" data-testid={`order-dedication-${it.book_id}`}>Signed bookplate: “{it.dedication}”</p>}
                {it.format === "ebook" && paid && (
                  <button onClick={() => dl(it.book_id, `/orders/${order.order_id}/ebooks/${it.book_id}`, `${it.title}.pdf`)} disabled={busy === it.book_id} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60" data-testid={`download-ebook-${it.book_id}`}>
                    <DownloadSimple weight="bold" /> {busy === it.book_id ? "Preparing…" : "Download PDF"}
                  </button>
                )}
              </div>
              <span className="font-semibold">{inr(it.line_total)}</span>
            </li>
          ))}
        </ul>
        <aside className="md:col-span-5">
          <div className="rounded-3xl bg-white p-6 ring-1 ring-border/70">
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Subtotal</dt><dd>{inr(order.subtotal)}</dd></div>
              {order.discount > 0 && <div className="flex justify-between text-accent"><dt>Discount ({order.coupon_code})</dt><dd>− {inr(order.discount)}</dd></div>}
              {order.shipping > 0 && <div className="flex justify-between"><dt className="text-muted-foreground">Shipping</dt><dd>{inr(order.shipping)}</dd></div>}
              <div className="flex justify-between text-xs text-muted-foreground"><dt>GST included</dt><dd>{inr(order.tax)}</dd></div>
              <div className="flex justify-between border-t border-border pt-3 text-base font-semibold"><dt>Total paid</dt><dd data-testid="order-detail-total">{inr(order.total)}</dd></div>
            </dl>
            {paid && (
              <button onClick={() => dl("invoice", `/orders/${order.order_id}/invoice`, `${order.invoice_number}.pdf`)} disabled={busy === "invoice"} className="mt-5 flex w-full items-center justify-center gap-2 rounded-full border border-foreground/20 py-2.5 text-sm font-semibold hover:bg-muted disabled:opacity-60" data-testid="download-invoice-button">
                <Receipt weight="duotone" /> {busy === "invoice" ? "Generating…" : `GST invoice ${order.invoice_number || ""}`}
              </button>
            )}
            <p className="mt-4 text-[11px] leading-relaxed text-muted-foreground">Ebooks are stamped with your name and email. All sales are final.</p>
          </div>
        </aside>
      </section>
    </div>
  );
}
